/**
 * This function converts an array of objects into a CSV string
 * This function DOES NOT support nested objects etc
 * @param objArray the object to convert
 * @param headerToKeyMap is a list of objects that hold the CSV column
 *        names and corresponding object keys and additional rules
 *        [{headerValue: value of the header,
 *          objKey: actual key in the object,
 *          inQuotes: put the value into quotation marks},..]
 * @param separator specify your own seperator, default is a comma
 * @param includeSeparator you can explicitly set the separator on top of the file
 *        so programs like excel know how to parse the fields
 * @return CSV string of the object
 * */
export function convertToCSV(
  objArray,
  headerToKeyMapObj,
  separator = ',',
  includeSeparator = false
) {
  const lineSeparator = '\r\n';
  let csvString = '';
  let currLine = '';
  // Explicitly set the separator on top of the file
  if (includeSeparator) {
    csvString += `sep=${separator}${lineSeparator}`;
  }
  // Generate the header line
  const headerList = [];
  headerToKeyMapObj.forEach((header) => {
    if (header.subFields) {
      header.subFields.forEach((subField) => {
        headerList.push(subField.exportHeader);
      });
    } else {
      headerList.push(header.headerValue);
    }
  });
  csvString += headerList.join(separator) + lineSeparator;

  // Loop over all objects
  objArray.forEach((obj) => {
    currLine = '';
    headerToKeyMapObj.forEach((header) => {
      if (header.subFields) {
        header.subFields.forEach((subField) => {
          let value = obj[header.objKey]?.[subField.key] ?? '';
          if (Array.isArray(value)) {
            value = value.join(';');
          }
          if (value !== undefined && value !== '') {
            currLine += subField.inQuotes ? `"${value}"${separator}` : `${value}${separator}`;
          } else {
            currLine += separator;
          }
        });
      } else {
        const value = obj[header.objKey];
        // Undefined values and empty strings should not be included
        if (value !== undefined && value !== '') {
          // Put the value into quotation marks if marked inQuotes
          currLine += header.inQuotes ? `"${value}"${separator}` : `${value}${separator}`;
        } else {
          currLine += separator;
        }
      }
    });
    // Trim the trailing comma from the end of line string
    currLine.slice(0, -1);
    currLine += lineSeparator;
    csvString += currLine;
  });
  return csvString;
}

/**
 * This function exports a string into utf-8 encoded file and starts the download
 * This function DOES NOT support nested objects etc
 * @param fileContents the string that will be included in the file
 * @param fileTitle name of the file. default title is "export"
 * @param extension the extension of the file. default extension is ".csv"
 * */
export function exportFile(fileContents, fileTitle = 'export', extension = '.csv') {
  const exportedFilename = fileTitle + extension;

  const blob = new Blob([fileContents], { type: 'text/csv;charset=utf-8;' });
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, exportedFilename);
  } else {
    const link = document.createElement('a');
    if (link.download !== undefined) {
      // feature detection
      // Browsers that support HTML5 download attribute
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', exportedFilename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}
